import { useState, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";
import { subscriptionStyle, LOGO_SRC, fmt } from "./sharedStyle";
import SquareCardForm from "./SquareCardForm";
import { predictFirstPickupDate, formatPickupDate } from "./predictFirstPickup";

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

export default function SignupPage() {
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [cart, setCart] = useState({});
  const [customerInfo, setCustomerInfo] = useState({ name: "", email: "", phone: "" });
  const [coverProcessingFee, setCoverProcessingFee] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(false);
  const [firstPickupLabel, setFirstPickupLabel] = useState(null);
  const firstPickupPreview = formatPickupDate(predictFirstPickupDate());

  useEffect(() => {
    supabase
      .from("products")
      .select("*")
      .eq("visible", true)
      .order("sort_order")
      .then(({ data }) => {
        setProducts(data || []);
        setProductsLoading(false);
      });
  }, []);

  const setQty = (id, qty) => {
    setCart((prev) => {
      const next = { ...prev };
      const key = String(id);
      if (qty <= 0) delete next[key];
      else next[key] = qty;
      return next;
    });
  };

  const baseItems = Object.entries(cart)
    .filter(([, qty]) => qty > 0)
    .map(([id, qty]) => {
      const p = products.find((p) => String(p.id) === id);
      return p ? { name: p.name, quantity: qty, price: p.price } : null;
    })
    .filter(Boolean);

  const baseTotal = baseItems.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const processingFee = parseFloat((baseTotal * 0.026 + 0.1).toFixed(2));
  const canSubmit = customerInfo.name.trim() && customerInfo.email.trim() && baseItems.length > 0;

  const handleTokenize = async (token, tokenError) => {
    if (tokenError) { setSubmitError(tokenError); return; }
    if (!token) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch("/api/subscription-signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...customerInfo, baseItems, cardNonce: token, coverProcessingFee }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong.");
      setFirstPickupLabel(data.firstPickupLabel || firstPickupPreview);
      setDone(true);
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="gg-app">
        <style>{subscriptionStyle}</style>
        <header className="gg-header">
          <div className="gg-logo"><img src={LOGO_SRC} alt="Grind and Gather" /></div>
        </header>
        <div className="gg-page">
          <h1>You're in! 🌾</h1>
          <p>Here's the simple version of how this works:</p>
          <p>Every Tuesday, we'll email you a quick link to add extras for the week — or hit skip if you need to. Don't do anything, and your order goes through just as you built it, card charged automatically.</p>
          <p>Saturday at 10pm is your cutoff. Anything you change before then applies to this week.</p>
          <p>Your card's stored safely with Square and only ever charged when you confirm, or right at the Saturday cutoff.</p>
          <p><strong>Your first pickup: {firstPickupLabel}.</strong></p>
          <p>That's it — welcome to the easy button.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gg-app">
      <style>{subscriptionStyle}</style>
      <header className="gg-header">
        <div className="gg-logo"><img src={LOGO_SRC} alt="Grind and Gather" /></div>
        <a href="mailto:grindgather@gmail.com" className="gg-contact-link">Get in Touch</a>
      </header>

      <div className="gg-page">
        <h1>Gather Your Favorites</h1>
        <p>One less thing on your list. Pick what you want, and it just shows up — every week.</p>

        <div className="gg-section-divider" />

        <h2>Choose Your Weekly Items</h2>
        {productsLoading && <p className="gg-loading">Loading this week's menu…</p>}
        {!productsLoading && (
          <div className="gg-card">
            {products.map((p) => {
              const qty = cart[String(p.id)] || 0;
              return (
                <div key={p.id} className="gg-item-row">
                  <span>{p.name} — {fmt(p.price)}</span>
                  <div className="gg-qty-control">
                    <button className="gg-qty-btn" onClick={() => setQty(p.id, qty - 1)} disabled={qty === 0}>−</button>
                    <span style={{ minWidth: 18, textAlign: "center" }}>{qty}</span>
                    <button className="gg-qty-btn" onClick={() => setQty(p.id, qty + 1)}>+</button>
                  </div>
                </div>
              );
            })}
            {baseItems.length > 0 && (
              <div className="gg-item-row" style={{ fontWeight: 500, marginTop: 8 }}>
                <span>Weekly Total</span>
                <span>{fmt(baseTotal)}</span>
              </div>
            )}
          </div>
        )}

        <p>
          Every Tuesday, we'll send you a quick link to toss in something extra for that week — or skip it if you
          need to. Miss it, and your usual order rolls out just as you built it.
        </p>

        <h2>Your Info</h2>
        {[
          { key: "name", label: "Full Name", type: "text", placeholder: "Sara Smith" },
          { key: "email", label: "Email Address", type: "email", placeholder: "sara@example.com" },
          { key: "phone", label: "Phone Number", type: "tel", placeholder: "(555) 000-0000" },
        ].map((field) => (
          <div key={field.key} className="gg-form-group">
            <label className="gg-form-label">{field.label}</label>
            <input
              className="gg-form-input"
              type={field.type}
              placeholder={field.placeholder}
              value={customerInfo[field.key]}
              onChange={(e) => setCustomerInfo((p) => ({ ...p, [field.key]: e.target.value }))}
            />
          </div>
        ))}

        <div className="gg-section-divider" />
        <h2>Card on File</h2>
        <p>
          Your card's safe with Square, and it's only charged when you confirm — or automatically at Saturday,
          10pm, if you haven't made changes.
        </p>
        <p>If you sign up now, your first pickup will be <strong>{firstPickupPreview}</strong>.</p>

        <div className="gg-card" style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <input
            type="checkbox"
            id="coverProcessingFee"
            checked={coverProcessingFee}
            onChange={(e) => setCoverProcessingFee(e.target.checked)}
            style={{ marginTop: 2, accentColor: "#8C9E82", width: 16, height: 16, flexShrink: 0, cursor: "pointer" }}
          />
          <label htmlFor="coverProcessingFee" style={{ fontSize: 13, fontWeight: 300, color: "#1E1C18", lineHeight: 1.6, cursor: "pointer" }}>
            Cover the card processing fee ({fmt(processingFee)}) every week — helps us keep prices where they are.
          </label>
        </div>

        {canSubmit ? (
          <SquareCardForm onTokenize={handleTokenize} loading={submitting} error={submitError} buttonLabel={`Start Weekly Basket · ${fmt(coverProcessingFee ? baseTotal + processingFee : baseTotal)}/week`} />
        ) : (
          <div className="gg-card">
            <p style={{ marginBottom: 0, opacity: 0.6 }}>Choose at least one item and enter your name and email above to continue.</p>
          </div>
        )}
      </div>
    </div>
  );
}
